import { useCallback, useEffect, useState } from 'react';

import { MusicPaperContractConfigurationService } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { isEqual, isUndefined } from 'lodash';
import { Observable } from 'rxjs';
import { contractConfigurationClient } from 'src/api/client';
import { usePrevious } from 'src/hooks';

/**
 * Helper type representing a valid RECORD API client method name
 */
export type ApiName = keyof MusicPaperContractConfigurationService;

type ApiFunction<E extends ApiName> = MusicPaperContractConfigurationService[E];
type ArgOf<T> = T extends (p: infer A) => any ? A : never;
type ExtractResponseType<R> = R extends Observable<infer X> ? X : never;

/**
 * Helper type which derives the RECORD API input type from the name
 */
export type InputOf<P extends ApiName> = ArgOf<ApiFunction<P>>;

/**
 * Helper type which derives the RECORD API output type from the name
 */
export type OutputOf<E extends ApiName> = ExtractResponseType<ReturnType<ApiFunction<E>>>;

export interface IApiResponse<E extends ApiName> {
    /**
     * The result of calling the API, or undefined if the call is not complete
     */
    response: OutputOf<E> | undefined;

    /**
     * Callback to force the API to be called again with the same payload.
     */
    refresh: () => void;

    /**
     * Any error object which was returned when calling the API
     */
    error: any | undefined;

    /**
     * True if there is an API call happening asynchronously
     */
    loading: boolean;
}

/**
 * Custom hook that calls the given API endpoint with the given payload, and
 * returns the response. The hook will initially return undefined while the
 * API runs asynchronously, then update the response once the call is complete.
 *
 * This hook calls the API once when the component mounts, and will call the API
 * again if the endpoint or payload changes.
 * @param endpoint the RECORD service endpoint to call
 * @param payload the payload to send to the endpoint
 */
function useApiCall<E extends ApiName>(endpoint: E, payload: InputOf<E>): IApiResponse<E> {
    const [value, setValue] = useState<OutputOf<E> | undefined>();
    const [error, setError] = useState<any | undefined>();
    const [loading, setLoading] = useState(false);
    const previousEndpoint = usePrevious(endpoint);
    const previousPayload = usePrevious(payload);

    const callApi = useCallback(() => {
        setLoading(true);
        const observable = contractConfigurationClient[endpoint](payload as any);
        observable.subscribe({
            next: (response) => {
                setLoading(false);
                setError(undefined);
                setValue(response as OutputOf<E>);
            },
            error: (e) => {
                setLoading(false);
                setValue(undefined);
                setError(e);
            }
        });
    }, [endpoint, payload]);

    useEffect(() => {
        if (isEqual(previousPayload, payload) && isEqual(previousEndpoint, endpoint)) {
            return;
        } else {
            callApi();
        }
    }, [endpoint, payload]);

    return {
        response: value,
        refresh: callApi,
        error: error,
        loading: loading
    };
}

// there is some weirdness with jest mocking of contractConfigurationClient
// which causes useApiCall not to see the mock
// we export useApiCall as a field to allow it to be mocked more easily
// since we shouldn't rely on mocking contractConfigurationClient
export const apiHooks = { useApiCall };
